/**
 * The aerodromes: a chart symbol at each end of a route that has an aircraft on it right now.
 *
 * The glyph is what makes a hairline read as traffic into a place rather than as a line drawn on the
 * globe, and it is also the pointer's own second answer: hovering it names the airport, the way
 * hovering an aircraft names the flight. Deduped by end and by city, because several routes into
 * one city share an airport at that end and the layer's own filter is the city.
 *
 * The symbol's art is `atlas-glyph-art.ts`'s; which of the drawn glyphs survive the declutter is
 * decided by the same rule the fleet uses (`atlas-declutter.ts`).
 */

import { legPhaseAt } from "@noko-city/shared";

import { byKey, declutter, type ScreenPoint } from "@/components/atlas-declutter";
import { type PlacedLeg, pointKey } from "@/components/atlas-legs";

/** The aerodrome symbol in the resting ink, and the same symbol in the hover ink. */
export const PORT_ICON = "noko-port";
export const PORT_HOVER_ICON = "noko-port-hot";

/** The airport glyphs the routes end at: one layer at rest, one for the glyph the pointer is on. */
export const PORT_SOURCE = "noko-flight-ports";
export const PORT_LAYER = "noko-flight-ports";
export const PORT_HOVER_LAYER = "noko-flight-ports-hot";

/** The airports' own weight: they anchor a route, so they read softer than the aircraft on it. */
export const PORT_BASE_OPACITY = 0.5;
/** The glyph the pointer is over, taken to the same prominence the hovered city's ink gets. */
export const PORT_HOVER_OPACITY = 0.9;
/** How much room one airport glyph reserves, as a multiple of its own drawn size. */
const PORT_CLEARANCE = 1.5;

/**
 * One airport glyph: an end of a route that has an aircraft on it right now.
 *
 * `key` is the glyph's own identity — the city, the end and the point — and is what the hover ink
 * selects on, because it is the one value two routes into the same airport can agree on.
 */
export type PortFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    city: string;
    kind: "from" | "to";
    code: string;
    key: string;
    /** The bow of the route this end belongs to: the declutter's order among equal ends. */
    lane: number;
  };
};

/**
 * The airports the drawn routes hang from: both ends of every route with an aircraft on it.
 *
 * Deduped by end rather than by route — several routes into one city share the airport at that end —
 * and by city as well, because the layer's own filter is the city, and one glyph shared between two
 * cities' routes would only survive for one of them.
 */
export const portFeatures = (legs: readonly PlacedLeg[], now: Date): PortFeature[] => {
  const features: PortFeature[] = [];
  const drawn = new Set<string>();
  for (const leg of legs) {
    if (legPhaseAt(leg, null, now) !== "in_progress") continue;
    const ends = [
      { kind: "from" as const, point: leg.from, code: leg.fromCode },
      { kind: "to" as const, point: leg.to, code: leg.toCode },
    ];
    for (const end of ends) {
      const key = `${leg.city}|${end.kind}|${pointKey(end.point)}`;
      if (drawn.has(key)) continue;
      drawn.add(key);
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [end.point.lng, end.point.lat] },
        properties: { city: leg.city, kind: end.kind, code: end.code, key, lane: leg.laneRadians },
      });
    }
  }
  return features;
};

/**
 * The airport glyphs that are drawn: the active city's own airport first, then the origins in lane
 * order, so the busiest bundle keeps its ends when the map is too small to show them all.
 */
export const declutterPorts = (
  ports: readonly PortFeature[],
  project: (coordinates: [number, number]) => ScreenPoint,
  iconPx: number,
  activeCity: string | null,
): PortFeature[] => {
  const priority = (port: PortFeature): number =>
    port.properties.city === activeCity && port.properties.kind === "to" ? 0 : 1;
  return declutter(
    ports,
    project,
    iconPx * PORT_CLEARANCE,
    (a, b) =>
      priority(a) - priority(b) ||
      Math.abs(a.properties.lane) - Math.abs(b.properties.lane) ||
      byKey(a, b),
  );
};
